import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, ChevronRight, Minus, Plus, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { fetchProduct, money } from "../lib/api";
import { klaviyoViewedProduct } from "../lib/klaviyo";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({});
  const [activeImg, setActiveImg] = useState(0);
  const [quickView, setQuickView] = useState(null);
  const { addItem, setOpen } = useCart();

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setQty(1);
    setSelected({});
    setActiveImg(0);
    fetchProduct(slug)
      .then((d) => {
        setData(d);
        const defaults = {};
        d.product.options.forEach((o) => {
          if (o.values.length) defaults[o.name] = o.values[0].label;
        });
        setSelected(defaults);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const product = data?.product;

  useEffect(() => {
    if (product) klaviyoViewedProduct(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.product_id]);

  const markup = useMemo(() => {
    if (!product) return 0;
    return product.options.reduce((sum, opt) => {
      const v = opt.values.find((x) => x.label === selected[opt.name]);
      return sum + (v?.markup || 0);
    }, 0);
  }, [product, selected]);

  if (notFound) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6" data-testid="product-not-found">
        <h1 className="font-display text-3xl font-black">Product not found</h1>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-brand-ink px-8 py-3 text-sm font-bold text-white">
          BACK TO SHOP
        </Link>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="product-loading">
        <div className="grid animate-pulse gap-10 lg:grid-cols-2">
          <div className="aspect-square bg-neutral-100" />
          <div className="space-y-4 pt-6">
            <div className="h-8 w-2/3 bg-neutral-100" />
            <div className="h-5 w-1/4 bg-neutral-100" />
            <div className="h-24 w-full bg-neutral-100" />
          </div>
        </div>
      </main>
    );
  }

  const images = [product.image, ...(product.gallery || [])].filter(Boolean);
  const unitPrice = Math.round((product.price + markup) * 100) / 100;
  const optLabel = product.options.map((o) => selected[o.name]).filter(Boolean).join(" / ");

  const add = () => {
    const ecwidOptions = {};
    product.options.forEach((o) => {
      if (selected[o.name]) ecwidOptions[o.ecwid_name || o.name] = selected[o.name];
    });
    addItem(product, qty, optLabel ? { label: optLabel, markup } : null, Object.keys(ecwidOptions).length ? ecwidOptions : null);
    toast.success("Added to cart");
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12" data-testid="product-page">
      <Seo title={`${product.name} | Farmacy District DC`} description={product.description?.slice(0, 155) || `${product.name} — premium cannabis delivered across Washington, DC.`} />

      <nav className="mb-6 flex items-center gap-1.5 text-xs text-neutral-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-brand-red">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/shop" className="hover:text-brand-red">Shop</Link>
        {product.categories[0] && (
          <>
            <ChevronRight className="h-3 w-3" />
            <span>{product.categories[0]}</span>
          </>
        )}
      </nav>

      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="relative aspect-square overflow-hidden bg-brand-sand" data-testid="product-image-main">
            {images.length > 0 ? (
              <img src={images[activeImg]} alt={product.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-neutral-400">Image coming soon</div>
            )}
            <div className="absolute left-4 top-4 flex flex-col gap-2">
              {product.on_sale && <span className="rounded-full bg-brand-red px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Sale</span>}
              {!product.in_stock && <span className="rounded-full bg-brand-ink px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Sold Out</span>}
            </div>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto scrollbar-hide" data-testid="product-gallery">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`h-20 w-20 flex-shrink-0 overflow-hidden border-2 transition-colors ${i === activeImg ? "border-brand-ink" : "border-transparent"}`}
                >
                  <img src={img} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">{product.categories[0] || "Farmacy District"}</p>
          <h1 className="mt-2 font-display text-3xl font-black leading-tight tracking-tighter sm:text-4xl" data-testid="product-name">
            {product.name}
          </h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-display text-3xl font-black" data-testid="product-price">{money(unitPrice)}</span>
            {product.compare_at && <span className="text-lg text-neutral-400 line-through">{money(product.compare_at + markup)}</span>}
          </div>
          <p className={`mt-2 text-xs font-bold uppercase tracking-wider ${product.in_stock ? "text-green-600" : "text-brand-red"}`} data-testid="product-availability">
            {product.in_stock ? "In stock — ready for delivery" : "Currently sold out"}
          </p>

          {product.options.map((opt) => (
            <div key={opt.name} className="mt-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">{opt.name}</p>
              <div className="flex flex-wrap gap-2">
                {opt.values.map((v) => (
                  <button
                    key={v.label}
                    onClick={() => setSelected((s) => ({ ...s, [opt.name]: v.label }))}
                    data-testid={`option-${opt.name.toLowerCase()}-${v.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                      selected[opt.name] === v.label
                        ? "border-brand-ink bg-brand-ink text-white"
                        : "border-neutral-300 hover:border-brand-ink"
                    }`}
                  >
                    {v.label}
                    {v.markup > 0 && <span className="ml-1.5 text-xs opacity-70">+{money(v.markup)}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-8 hidden items-stretch gap-3 md:flex">
            <div className="flex items-center border border-neutral-300">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 transition-colors hover:bg-neutral-100" aria-label="Decrease quantity" data-testid="qty-minus">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 transition-colors hover:bg-neutral-100" aria-label="Increase quantity" data-testid="qty-plus">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => add()}
              disabled={!product.in_stock}
              data-testid="add-to-cart-button"
              className="flex-1 rounded-full bg-brand-lime py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400"
            >
              {product.in_stock ? `ADD TO CART — ${money(unitPrice * qty)}` : "SOLD OUT"}
            </button>
          </div>

          {product.requires_id && (
            <p className="mt-4 flex items-start gap-2 bg-brand-sand p-3.5 text-xs leading-relaxed text-neutral-600" data-testid="id-verification-note">
              <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-ink" />
              21+ verification required: you'll confirm your age at checkout and show valid ID on delivery.
            </p>
          )}

          <div className="mt-6 grid grid-cols-3 gap-3 border-y border-neutral-200 py-5 text-center">
            {[
              { icon: Truck, label: "30–90 min DC delivery" },
              { icon: ShieldCheck, label: "Discreet packaging" },
              { icon: BadgeCheck, label: "I-71 compliant" },
            ].map((t) => (
              <div key={t.label} className="flex flex-col items-center gap-1.5">
                <t.icon className="h-5 w-5 text-brand-ink" />
                <span className="text-[11px] font-semibold text-neutral-500">{t.label}</span>
              </div>
            ))}
          </div>

          {product.description && (
            <div className="mt-8" data-testid="product-description">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Details</h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-neutral-600">{product.description}</p>
            </div>
          )}
          {product.sku && <p className="mt-6 text-xs text-neutral-400">SKU: {product.sku}</p>}
        </div>
      </div>

      {data.related.length > 0 && (
        <section className="mt-20 sm:mt-28" data-testid="related-products">
          <Reveal>
            <div className="mb-8 flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">You May Also Like</h2>
              <Link to="/shop" className="text-sm font-bold text-brand-red underline-offset-4 hover:underline">Shop all</Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 gap-y-10 md:grid-cols-4 md:gap-6">
            {data.related.map((p) => (
              <ProductCard key={p.product_id} product={p} onQuickView={setQuickView} />
            ))}
          </div>
        </section>
      )}

      {product.in_stock && (
        <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center gap-3 border-t border-neutral-200 bg-white/95 p-3 backdrop-blur-xl md:hidden" data-testid="mobile-sticky-cart-bar">
          <div className="flex items-center border border-neutral-300">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5" aria-label="Decrease quantity">
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-7 text-center text-sm font-bold">{qty}</span>
            <button onClick={() => setQty(qty + 1)} className="p-2.5" aria-label="Increase quantity">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => add()}
            data-testid="mobile-add-to-cart-button"
            className="flex-1 rounded-full bg-brand-lime py-3.5 text-sm font-bold text-brand-ink transition-colors hover:bg-brand-limehover"
          >
            ADD TO CART — {money(unitPrice * qty)}
          </button>
        </div>
      )}

      <QuickView slug={quickView} onClose={() => setQuickView(null)} />
    </main>
  );
}
